import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import { t } from './i18n';
import { CercetareTema } from './CercetareTema';

// ============================================================
// Temele zilei (15 octombrie 2026, TEME_PLAN.md, supabase/temele_zilei.sql).
//
// Pe clientul ales: sus tabla ultimei citiri a radarului (temele mari, cu numarul de publicatii),
// dedesubt propunerile de reactie pentru el, numerotate („Nr. 12" = numarul cu care Luminita
// alege si in chat, cu clientul: „aleg 5 la Pandea”). Numerele sunt pe client (`nr_client`, 15.10.2026);
// `nr` ramane cheia interna. Propunerile le pune Claude prin priza (`pune_teme`); aici doar se aleg.
// Cine vede: super adminul, managerul clientului, si contul de client doar cu bifa
// `vede_temele_zilei` (atunci vede tot ce e al lui, dar nu apasa). „Aleg" / „Nu" trec prin
// functia `schimba_stadiu_tema`, care lasa doar agentia.
// In tabla nu apar alti clienti: din propuneri se arata doar ale clientului ales.
// Sub tema aleasa: cercetarea ei (18 octombrie 2026, CERCETARE_PLAN.md, `CercetareTema.js`). „Aleg”
// o pune la coada (declansatorul din baza); clientul o vede doar cu bifa `vede_cercetarea` (RLS).
// ============================================================

// O tema aleasa ramane la locul ei in lista (Luminita, 18.10.2026: „de ce s-o mut la coada listei
// dupa ce am apasat Aleg?”); stadiul se vede pe carton. Cele lasate deoparte au fila lor, cu cercetarea
// pastrata, ca sa poata fi refolosita (18.10.2026: „poate cercetarea imi prinde bine la altceva”).
const STADII_FILA = { propuneri: ['propusa', 'aleasa', 'folosita'], deoparte: ['respinsa'] };
const RISC = { verde: '#2E9E5B', galben: '#C98A1B', rosu: '#C2362B' };

const carton = { background: 'white', border: '1.5px solid #D1E8DA', borderRadius: 14, padding: '14px 18px' };
const eticheta = { fontSize: 12, fontWeight: 600, color: '#4B5563', letterSpacing: '0.5px', textTransform: 'uppercase' };
const pastila = { fontSize: 12, fontWeight: 600, borderRadius: 999, padding: '2px 9px', background: '#EEF6F1', color: '#374151', whiteSpace: 'nowrap' };

const cand = (iso) => (iso ? new Date(iso).toLocaleString('ro-RO', { timeZone: 'Europe/Bucharest', weekday: 'long', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }) : '');

function Legatura({ href, children }) {
  if (!/^https:\/\//.test(href || '')) return <span>{children}</span>;
  return <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#1A6B3C' }}>{children}</a>;
}

export function TemeleZilei({ client, rol }) {
  const poateAlege = rol === 'super_admin' || rol === 'manager';
  const [citire, setCitire] = useState(null);
  const [teme, setTeme] = useState({});
  const [cercetari, setCercetari] = useState({});
  const [propuneri, setPropuneri] = useState(null);
  const [lucrez, setLucrez] = useState(null);
  const [eroare, setEroare] = useState('');
  // Se deschide mereu pe propuneri; tabla presei doar la cerere (Luminița, 15.10.2026).
  const [tab, setTab] = useState('propuneri');

  const incarca = useCallback(async () => {
    setEroare('');
    const [{ data: c, error: e1 }, { data: p, error: e2 }] = await Promise.all([
      supabase.from('teme_citiri').select('id, cand, sursa, nota, tabla, deoparte').order('id', { ascending: false }).limit(1),
      supabase.from('teme_propuneri')
        .select('nr, nr_client, tema_id, unghi, motiv, risc, de_verificat, stadiu, nota, propusa_la, grup_id')
        .eq('client_id', client.id)
        .gte('propusa_la', new Date(Date.now() - 30 * 86400000).toISOString())
        .order('nr_client', { ascending: false })
        .limit(200),
    ]);
    if (e1 || e2) { setEroare(t('teme', 'nuSeIncarca')); setPropuneri([]); return; }
    const ultima = (c || [])[0] || null;
    const ids = new Set([...(p || []).map(x => x.tema_id), ...((ultima && ultima.tabla) || []).map(x => x.tema_id)]);
    let harta = {};
    if (ids.size) {
      const { data: tz } = await supabase.from('teme_zilei').select('id, link, titlu, sectiune, publicatii, in_crestere, rezumat').in('id', [...ids]);
      harta = Object.fromEntries((tz || []).map(x => [x.id, x]));
    }
    // Cercetarile: RLS le da doar cui are voie (clientul fara bifa primeste lista goala).
    let cerc = {};
    const nrs = (p || []).filter(x => x.stadiu !== 'propusa').map(x => x.nr);
    if (nrs.length) {
      const { data: tc } = await supabase.from('teme_cercetari')
        .select('propunere_nr, cerere, stare, continut, nota, postare_text, intrebare, intrebare_la, raspuns, raspuns_la, raspuns_de_nume, pornita_la, gata_la')
        .in('propunere_nr', nrs);
      cerc = Object.fromEntries((tc || []).map(x => [x.propunere_nr, x]));
    }
    setCitire(ultima);
    setTeme(harta);
    setCercetari(cerc);
    setPropuneri(p || []);
  }, [client.id]);

  useEffect(() => { setPropuneri(null); setCitire(null); setTab('propuneri'); incarca(); }, [incarca]);

  async function schimba(nr, stadiu) {
    setLucrez(nr); setEroare('');
    const { error } = await supabase.rpc('schimba_stadiu_tema', { p_nr: nr, p_stadiu: stadiu });
    setLucrez(null);
    if (error) { setEroare(t('teme', 'nuSaSchimbat')); return; }
    setPropuneri(prev => prev.map(x => (x.nr === nr ? { ...x, stadiu } : x)));
    incarca();   // „Aleg” pune cercetarea la coada, „Nu” o scoate: le recitim
  }

  const alePropuse = {};
  (propuneri || []).forEach(x => { alePropuse[x.tema_id] = x.nr_client ?? x.nr; });
  const tabla = ((citire && citire.tabla) || []).slice().sort((a, b) => (b.publicatii || 0) - (a.publicatii || 0));
  const deoparte = (citire && citire.deoparte) || [];
  const asteapta = (propuneri || []).filter(x => x.stadiu === 'propusa').length;
  const nDeoparte = (propuneri || []).filter(x => x.stadiu === 'respinsa').length;
  const lista = STADII_FILA[tab] ? (propuneri || []).filter(x => STADII_FILA[tab].includes(x.stadiu)) : [];

  return (
    <div className="pagina">
      <div className="pagina-header">
        <div>
          <h1 className="pagina-titlu">{t('teme', 'titlu')}</h1>
          <p className="pagina-subtitlu">{t('teme', 'subtitlu')}</p>
        </div>
      </div>

      {eroare && <div style={{ background: '#FEE2E2', color: '#B91C1C', borderRadius: 10, padding: '10px 12px', marginBottom: 14, fontSize: 14 }}>{eroare}</div>}

      {propuneri === null ? <p style={{ color: '#4B5563' }}>{t('teme', 'seIncarca')}</p> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
          <div className="pc-taburi" role="tablist" style={{ marginBottom: 0 }}>
            <button type="button" role="tab" aria-selected={tab === 'propuneri'} className={`pc-tab ${tab === 'propuneri' ? 'activ' : ''}`} onClick={() => setTab('propuneri')}>
              {t('teme', 'tabPropuneri')}{asteapta > 0 && <span className="pc-tab-nr">{asteapta}</span>}
            </button>
            <button type="button" role="tab" aria-selected={tab === 'presa'} className={`pc-tab ${tab === 'presa' ? 'activ' : ''}`} onClick={() => setTab('presa')}>
              {t('teme', 'tabla')}
            </button>
            <button type="button" role="tab" aria-selected={tab === 'deoparte'} className={`pc-tab ${tab === 'deoparte' ? 'activ' : ''}`} onClick={() => setTab('deoparte')}>
              {t('teme', 'stadiu_respinsa')}{nDeoparte > 0 && <span className="pc-tab-nr">{nDeoparte}</span>}
            </button>
          </div>
          {STADII_FILA[tab] && (
          <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <span style={eticheta}>{tab === 'propuneri' ? t('teme', 'propuneri', { client: client.nume }) : t('teme', 'stadiu_respinsa')}</span>
              {tab === 'propuneri' && asteapta > 0 && <span style={{ fontSize: 13, color: '#8F5310', fontWeight: 600 }}>{t('teme', 'asteapta', { n: asteapta })}</span>}
            </div>
            {!lista.length && <p style={{ color: '#4B5563', fontSize: 14, margin: 0 }}>{t('teme', tab === 'propuneri' ? 'niciuna' : 'niciunaDeoparte')}</p>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {lista.map(x => {
                    const tm = teme[x.tema_id];
                    const st = x.stadiu;
                    return (
                      <div key={x.nr} style={{ ...carton, borderColor: st === 'propusa' ? '#F1D9B5' : '#D1E8DA', display: 'flex', flexDirection: 'column', gap: 7, opacity: st === 'respinsa' ? 0.7 : 1 }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                          <span style={{ ...pastila, background: '#111827', color: 'white', fontVariantNumeric: 'tabular-nums' }}>{t('teme', 'nr', { nr: x.nr_client ?? x.nr })}</span>
                          {x.risc && RISC[x.risc] && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#4B5563' }}>
                              <i style={{ width: 9, height: 9, borderRadius: '50%', background: RISC[x.risc], display: 'inline-block' }} />{t('teme', 'risc_' + x.risc)}
                            </span>
                          )}
                          {(st === 'aleasa' || st === 'folosita') && <span style={{ ...pastila, background: '#1A6B3C', color: 'white' }}>{t('teme', 'eticheta_' + st)}</span>}
                          <span style={{ fontSize: 12.5, color: '#4B5563' }}>{t('teme', 'propusaLa', { cand: cand(x.propusa_la) })}</span>
                        </div>
                        <div style={{ fontWeight: 700, fontSize: 16, lineHeight: 1.35 }}>{x.unghi}</div>
                        {x.motiv && <div style={{ fontSize: 14, color: '#374151' }}>{x.motiv}</div>}
                        {tm && <div style={{ fontSize: 13.5, color: '#374151' }}>{t('teme', 'tema')} <Legatura href={tm.link}>{tm.titlu}</Legatura>{tm.publicatii != null ? ` · ${t('teme', 'nPublicatii', { n: tm.publicatii })}` : ''}</div>}
                        {x.de_verificat && <div style={{ fontSize: 13.5, background: '#FFF4E5', border: '1px solid #F1D9B5', borderRadius: 8, padding: '7px 10px' }}><b style={{ color: '#8F5310' }}>{t('teme', 'deVerificat')}</b> {x.de_verificat}</div>}
                        {x.nota && <div style={{ fontSize: 13, color: '#8F5310' }}>{t('teme', 'notaTa', { nota: x.nota })}</div>}
                        {st !== 'propusa' && (
                          <CercetareTema propunere={x} cercetare={cercetari[x.nr]} client={client} titlu={tm ? tm.titlu : null} rol={rol}
                            onSchimbat={incarca} onDeoparte={() => schimba(x.nr, 'respinsa')} />
                        )}
                        {poateAlege && (
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 2 }}>
                            {st === 'propusa' && <>
                              <button type="button" className="btn-principal" disabled={lucrez === x.nr} onClick={() => schimba(x.nr, 'aleasa')}>{t('teme', 'aleg')}</button>
                              <button type="button" className="btn-secundar" disabled={lucrez === x.nr} onClick={() => schimba(x.nr, 'respinsa')}>{t('teme', 'nu')}</button>
                            </>}
                            {(st === 'aleasa' || st === 'respinsa') && (
                              <button type="button" className="btn-secundar" disabled={lucrez === x.nr} onClick={() => schimba(x.nr, 'propusa')}>{t('teme', 'inapoi')}</button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
          </section>
          )}
          {tab === 'presa' && (
            <>
          <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <span style={eticheta}>{t('teme', 'tabla')}</span>
              {citire && <span style={{ fontSize: 13, color: '#4B5563' }}>{t('teme', 'citire', { cand: cand(citire.cand), sursa: citire.sursa || '' })}</span>}
            </div>
            {!tabla.length ? <p style={{ color: '#4B5563', fontSize: 14, margin: 0 }}>{t('teme', 'tablaGoala')}</p> : (
              <div style={{ ...carton, padding: '4px 16px' }}>
                {tabla.map((r, i) => {
                  const x = teme[r.tema_id];
                  if (!x) return null;
                  return (
                    <div key={r.tema_id} style={{ display: 'grid', gridTemplateColumns: '48px minmax(0,1fr)', gap: 12, padding: '11px 0', borderTop: i ? '1px solid #E4EFE8' : 'none' }}>
                      <div>
                        <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 24, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{r.publicatii ?? '–'}</div>
                        <div style={{ fontSize: 10.5, color: '#4B5563' }}>{t('teme', 'publicatii')}</div>
                      </div>
                      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 5 }}>
                        <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}><Legatura href={x.link}>{x.titlu}</Legatura></div>
                        {x.rezumat && <div style={{ fontSize: 13.5, color: '#374151' }}>{x.rezumat}</div>}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {x.sectiune && <span style={pastila}>{x.sectiune}</span>}
                          {r.noua && <span style={{ ...pastila, background: '#FFF4E5', color: '#8F5310' }}>{t('teme', 'noua')}</span>}
                          {r.fata_de_data_trecuta > 0 && <span style={{ ...pastila, background: '#E8F5EE', color: '#1A6B3C' }}>{t('teme', 'crestere', { n: r.fata_de_data_trecuta })}</span>}
                          {r.in_crestere && <span style={{ ...pastila, background: '#E8F5EE', color: '#1A6B3C' }}>{t('teme', 'inCrestere')}</span>}
                          {alePropuse[r.tema_id] && <span style={{ ...pastila, background: '#1A6B3C', color: 'white' }}>{t('teme', 'propusaLui', { nr: alePropuse[r.tema_id] })}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
          {deoparte.length > 0 && (
            <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={eticheta}>{t('teme', 'deoparte')}</span>
              <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 14 }}>
                {deoparte.map((d, i) => <li key={i}><b style={{ fontWeight: 600 }}>{d.titlu}</b>{d.motiv ? `: ${d.motiv}` : ''}</li>)}
              </ul>
            </section>
          )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
